import { useState, type ChangeEvent, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Briefcase, Building2, CheckCircle2, Loader2, Mail, User, Users, type LucideIcon } from 'lucide-react';
import AuthFrame from '../../components/layout/AuthFrame';
import { Sentry } from '../../lib/sentry';

const API_URL = import.meta.env.VITE_API_URL;

function InquiryField({ id, label, icon: Icon, ...props }: {
  id: string;
  label: string;
  icon: LucideIcon;
  type: string;
  value: string;
  placeholder: string;
  min?: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-2 block text-sm font-medium text-app-ink">{label}</label>
      <div className="relative">
        <Icon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-app-muted" aria-hidden="true" />
        <input id={id} className="input-field !pl-11" required {...props} />
      </div>
    </div>
  );
}

export default function OrgInquiry() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [orgName, setOrgName] = useState('');
  const [salesRepCount, setSalesRepCount] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    const repCount = parseInt(salesRepCount, 10);
    if (isNaN(repCount) || repCount < 1) {
      setError('Please enter a valid number of sales representatives');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/v1/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ full_name: fullName, email, role, organisation_name: orgName, sales_rep_count: repCount }),
      });
      const result = await response.json();
      if (!response.ok || result.error) {
        Sentry.captureException(new Error(result.error || `send-inquiry-email failed (${response.status})`), { tags: { context: 'org_inquiry' } });
        setError(result.error || 'Something went wrong. Please try again.');
        setLoading(false);
        return;
      }
      setSubmitted(true);
    } catch (requestError) {
      Sentry.captureException(requestError, { tags: { context: 'org_inquiry' } });
      setError(requestError instanceof Error ? requestError.message : 'Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <AuthFrame eyebrow="Request received" title="Thank you" description="We have received your enquiry and will contact you to configure your organisation workspace.">
        <div className="border-l-2 border-app-positive bg-app-surface p-5">
          <div className="flex gap-3">
            <CheckCircle2 className="h-6 w-6 shrink-0 text-app-positive" aria-hidden="true" />
            <div>
              <p className="font-semibold text-app-ink">Your request is with our team.</p>
              <p className="mt-2 text-sm leading-6 text-app-muted">We typically respond within one business day.</p>
            </div>
          </div>
        </div>
        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <Link to="/" className="btn-primary">Return to Heymakr</Link>
          <Link to="/login" className="btn-secondary">Sign in</Link>
        </div>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame eyebrow="Organisation access" title="Tell us about your team" description="Share the details required to prepare an appropriately governed Heymakr workspace.">
      <Link to="/signup" className="mb-6 inline-flex min-h-10 items-center gap-2 text-sm font-semibold text-app-muted hover:text-app-ink"><ArrowLeft className="h-4 w-4" aria-hidden="true" />Back</Link>
      {error ? <div className="mb-6 flex gap-3 border-l-2 border-app-critical bg-app-surface px-4 py-3" role="alert"><AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-app-critical" aria-hidden="true" /><p className="text-sm leading-5 text-app-ink">{error}</p></div> : null}
      <form onSubmit={handleSubmit} className="space-y-5">
        <InquiryField id="full-name" label="Full name" icon={User} type="text" value={fullName} onChange={event => setFullName(event.target.value)} placeholder="Prem Vyas" />
        <InquiryField id="work-email" label="Work email" icon={Mail} type="email" value={email} onChange={event => setEmail(event.target.value)} placeholder="you@company.com" />
        <InquiryField id="role" label="Role or job title" icon={Briefcase} type="text" value={role} onChange={event => setRole(event.target.value)} placeholder="VP of Sales" />
        <InquiryField id="organisation-name" label="Organisation name" icon={Building2} type="text" value={orgName} onChange={event => setOrgName(event.target.value)} placeholder="Acme" />
        <InquiryField id="sales-representatives" label="Number of sales representatives" icon={Users} type="number" min="1" value={salesRepCount} onChange={event => setSalesRepCount(event.target.value)} placeholder="10" />
        <button type="submit" disabled={loading} className="btn-primary w-full gap-2">{loading ? <><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />Submitting request</> : 'Submit request'}</button>
      </form>
      <p className="mt-6 text-sm text-app-muted">Already have an account? <Link to="/login" className="font-semibold text-app-ink underline decoration-app-line-strong underline-offset-4 hover:decoration-app-ink">Sign in</Link></p>
    </AuthFrame>
  );
}
